import Image from "next/image";
import { useState } from "react";
import { Product, ProductSlug } from "@/api/product-api";
import { Button } from "@/v2-components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/v2-components/ui/dialog";
import { useAddCartStoreItem, useCartStore } from "@/store/cart-store";
import { useAddedToBagStore } from "@/store/cart-store/useAddedToBagStore";
import { CartStoreUtils } from "@/store/cart-store/cart-store-utils";
import { useTrans } from "@/utils/useTrans";
import { QuantityCounter } from "./quantity-counter";
import { ProductUtil } from "@/utils/ProductUtil";
import { NotifyMe } from "@/v2-components/pages/tactsuit/NotifyMe";

type SkuOptionItem = NonNullable<Product["skuOptions"]>[number];

interface Props {
  open: boolean;
  product: Product;
  onClose: () => void;
  /**
   * 행(SKU)마다 **다른 제품**일 때 그 제품 slug. 없으면 전부 `product.slug` 로 담는다(기본).
   *
   * 사이즈 라인(이너글러브 S/M/L/XL — 사이즈마다 별도 제품)을 한 다이얼로그에 모을 때 쓴다
   * (`libs/product/size-line.ts` 의 `sizeLineDialogProduct`). 이게 없으면 M 을 담아도 카트 줄의
   * slug 가 대표(S) 제품이 되어, 카트·주문에서 제품과 SKU 가 어긋난다.
   */
  slugBySku?: Record<string, string>;
}

export function SkuOptionsDialog({ open, product, onClose, slugBySku }: Props) {
  const { t } = useTrans(["tactsuit"]);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const addCartStoreItem = useAddCartStoreItem();
  const cartItems = useCartStore((state) => state.items);
  const openAddedToBag = useAddedToBagStore((s) => s.open);

  const getMaxForSku = (sku: string): number => {
    return Math.max(
      0,
      CartStoreUtils.MAX_QUANTITY - CartStoreUtils.sumBySKU(cartItems, sku),
    );
  };

  const handleQuantityChange = (sku: string, delta: number) => {
    setQuantities((prev) => {
      const current = prev[sku] || 0;
      const newQuantity = current + delta;
      if (newQuantity < 0) return prev;

      if (delta > 0) {
        const max = getMaxForSku(sku);
        if (newQuantity > max) return prev;
      }

      return { ...prev, [sku]: newQuantity };
    });
  };

  const handleClose = () => {
    setQuantities({});
    onClose();
  };

  const handleAddToCart = () => {
    const itemsToAdd =
      product.skuOptions?.filter(
        (skuOption) => (quantities[skuOption.sku] || 0) > 0,
      ) || [];

    if (itemsToAdd.length === 0) return;

    const addedSkus: string[] = [];
    // 담는 중 수량 세트로 접혔나 — 접혔으면 훅이 병합 다이얼로그를 이미 열었다(#5945 R1).
    let merged = false;

    itemsToAdd.forEach((skuOption) => {
      if (skuOption.sku && skuOption.options) {
        const result = addCartStoreItem(
          {
            slug: (slugBySku?.[skuOption.sku] ?? product.slug) as ProductSlug,
            quantity: quantities[skuOption.sku],
            sku: skuOption.sku,
            options: ProductUtil.getOptionsFromSkuOption(skuOption),
          },
          // 이 다이얼로그는 제품 카드의 담기에서 열린다 (#5292)
          "product_card",
        );
        if (result.added) {
          addedSkus.push(skuOption.sku);
        }
        if (result.merged) merged = true;
      }
    });

    handleClose();
    /**
     * ★담기 뒤 **이동하지 않는다** — 형제 갈래(`tact-suit-card-notify-or-buy.tsx`)와 같은
     * 이유다 (#5480). 여기는 SKU 가 여럿일 수 있다 — 팝이 담은 SKU **전부**를 받는다.
     *
     * 이 다이얼로그는 색·사이즈를 **여러 개 동시에** 담는다(`itemsToAdd` = 수량 > 0 인
     * skuOption 전부). prod 에서 이 갈래로 오는 것은 액세서리 20개 중 **배지 7개**이고
     * 전부 색 2종이다(2026-09-03 `admin-apis /api/v1/shop-products` 실측).
     *
     * ```
     * 담긴 SKU 1개+  팝(#5366)      방금 담은 항목 **전부**를 GNB 아이콘 아래에 보여준다
     * 담긴 SKU 0개   아무것도       담기지 않았으므로 「담았다」고 말하지 않는다
     * ```
     *
     * ⚠️ 마지막 갈래는 **이 다이얼로그 단독 흐름에서는 도달하지 않는다**(리뷰 R1).
     * `handleQuantityChange` 가 `newQuantity > getMaxForSku(sku)` 를 막으므로 담을 수량이
     * 항상 `MAX − 기존` 이하이고, 따라서 `isOverItemLimit` 이 참이 될 수 없다. 그래도 두는
     * 이유는 판정을 **호출처의 반환값**에 맞춰 두는 것이고(상한 로직이 바뀌면 여기서
     * 갈라진다), 무해하다.
     *
     * 종전 주석은 이 자리에 *"Purchase Limit 안내가 이미 떠 있다"* 고 적었는데 **이
     * 다이얼로그에서는 사실이 아니다** — 그 상태는 형제 갈래(`NotifyOrBuy` 의 옵션 1개
     * 담기)에서만 난다.
     *
     * 종전엔 팝이 SKU **하나**만 받아서 2개 이상이면 `/cart` 로 이동했다(첫 SKU 만 띄우면 담은
     * 것보다 적게 말하므로). 사용자 지적(2026-10-07 · 배지 모든 타입 담기 → 카트 이동)으로 팝이
     * SKU 목록을 받게 했다(`useAddedToBagStore`). ⚠️ 첫 SKU 만 넘기도록 되돌리면 그 「적게 말하기」가
     * 다시 생긴다 — 목록째 넘긴다.
     */
    // ⚠️ 세트로 접혔으면(`merged`) 팝을 띄우지 않는다 — 접힌 SKU 줄은 카트에서 사라져 팝이 그 카드를
    // 못 그리고, 병합 다이얼로그가 이미 떠 있어 표면이 둘이 된다. 지금 세트 짝(MOLLE Pouch)은 SKU
    // 하나라 이 다이얼로그로 오지 않지만, 판정은 훅의 반환값에 맞춰 둔다.
    if (addedSkus.length > 0 && !merged) openAddedToBag(addedSkus);
  };

  const totalQuantity = Object.values(quantities).reduce(
    (sum, qty) => sum + qty,
    0,
  );

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-h-[80vh] max-w-[520px] overflow-y-auto p-9">
        <DialogHeader className="flex items-start gap-x-2.5">
          <DialogTitle className="text-2xl">
            {t("Select Options for", { ns: "gnb" })} {product.name}
          </DialogTitle>
          <div className="min-w-[24px] cursor-pointer">
            <DialogClose className="size-6" />
          </div>
        </DialogHeader>
        <div className="">
          <div className="space-y-2">
            {product.skuOptions.map((skuOption) => (
              <SkuOptionRow
                key={skuOption.sku}
                skuOption={skuOption}
                productSlug={slugBySku?.[skuOption.sku] ?? product.slug}
                quantity={quantities[skuOption.sku] || 0}
                maxQuantity={getMaxForSku(skuOption.sku)}
                onQuantityChange={handleQuantityChange}
              />
            ))}
          </div>
        </div>
        <DialogFooter className="justify-end gap-2">
          <Button
            className="px-6 py-3"
            size="sm"
            shape={"pill"}
            variant="blue-fill"
            onClick={handleAddToCart}
            disabled={totalQuantity === 0}
          >
            {t("Add to Bag", { ns: "gnb" })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * 다이얼로그의 한 행(옵션 하나).
 *
 * ★**품절 행은 수량 대신 「Notify me」** (#5922). 종전엔 행을 재고로 거르지 않고 수량 상한도 카트 한도(4)만
 * 봐서, 품절 옵션을 그대로 담을 수 있었다 — dev 실측(2026-10-06 · `classic-badge-for-tactsuit-air`, Gold 재고 0):
 * Gold `+` → 「Add to Bag」 → 카트 `badge-tactsuit-air-gold x1`. 품절 행에는 수량 버튼이 없으니 담기 수량이
 * 생길 수 없고, 대신 그 옵션의 재입고 알림을 그 자리에서 건다(목록 카드의 전부 품절 「Notify me」와 같은 모달).
 *
 * 알림 모달에는 이 행의 옵션을 미리 채운다(`initialColor`·`initialSize`) — 색이 여러 개인 배지는 모달이 색을
 * 다시 묻기 때문이다. 행마다 다른 제품이면(사이즈 라인) `productSlug` 가 그 제품이다.
 */
function SkuOptionRow({
  skuOption,
  productSlug,
  quantity,
  maxQuantity,
  onQuantityChange,
}: {
  skuOption: SkuOptionItem;
  productSlug: string;
  quantity: number;
  maxQuantity: number;
  onQuantityChange: (sku: string, delta: number) => void;
}) {
  const soldOut = (skuOption.stock ?? 0) <= 0;
  const options = ProductUtil.getOptionsFromSkuOption(skuOption);
  return (
    <div className="flex flex-wrap items-center justify-end gap-4 rounded-lg border border-coolgray-20 px-4 py-2 hover:border-coolgray-30">
      <div className="flex grow items-center gap-1">
        <div className="relative flex aspect-square w-[76px] flex-shrink-0 items-center">
          <Image
            src={skuOption.thumbnail}
            alt={skuOption.name}
            fill
            className="object-contain"
          />
        </div>
        <h3 className="truncate text-base font-500 text-coolgray-90">
          {skuOption.name}
        </h3>
      </div>

      {soldOut ? (
        <NotifyMe
          productSlug={productSlug}
          productSku={skuOption.sku}
          initialColor={options.find((o) => o.type === "color")?.value}
          initialSize={options.find((o) => o.type === "size")?.value}
          className="px-4 text-coolgray-90"
        />
      ) : (
        // 수량 조절
        <QuantityCounter
          value={quantity}
          decreaseDisabled={quantity === 0}
          increaseDisabled={quantity >= maxQuantity}
          onDecrease={() => onQuantityChange(skuOption.sku, -1)}
          onIncrease={() => onQuantityChange(skuOption.sku, 1)}
        />
      )}
    </div>
  );
}
